import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook, Send, MessageCircle } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { useSEO } from "@/hooks/useSEO";
import { BRAND, wa } from "@/lib/site";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.5c-1.3.1-2.5-.3-3.7-1v6.1c0 3.4-2.6 5.8-5.9 5.5-2.7-.2-4.8-2.5-4.7-5.2.1-2.7 2.4-4.8 5.1-4.7.3 0 .6 0 .9.1v2.6c-.3-.1-.6-.2-.9-.2-1.2 0-2.2 1-2.2 2.2s1 2.2 2.2 2.2 2.2-1 2.2-2.2V3h3.3z" />
  </svg>
);

const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(BRAND.address)}&output=embed`;

export default function Kontak() {
  useSEO(
    "Kontak",
    "Hubungi HATTORY KONVEKSI di Desa Kendung, Kwadungan, Ngawi, Jawa Timur. WhatsApp 0857-1312-8864, email hattorykonveksi@gmail.com."
  );

  const [form, setForm] = useState({ name: "", contact: "", need: "", message: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.need.trim()) {
      toast.error("Mohon lengkapi nama, kontak, dan kebutuhan Anda.");
      return;
    }
    const lines = [
      "Halo HATTORY KONVEKSI, saya ingin bertanya/memesan.",
      "",
      `Nama: ${form.name.trim()}`,
      `Kontak: ${form.contact.trim()}`,
      `Kebutuhan: ${form.need.trim()}`,
    ];
    if (form.message.trim()) lines.push(`Pesan: ${form.message.trim()}`);
    const waMsg = lines.join("\n");
    window.open(wa(waMsg), "_blank");
    toast.success("Membuka WhatsApp dengan pesan Anda...");
    setForm({ name: "", contact: "", need: "", message: "" });
  };

  return (
    <>
      <PageHero
        overline="Kontak"
        titleLines={["Mari", "Terhubung"]}
        description="Punya pertanyaan atau siap memesan? Hubungi kami melalui kanal di bawah ini, atau kirim pesan langsung lewat formulir."
      />

      <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left: info + map */}
          <div>
            <Reveal>
              <div className="space-y-5" data-testid="contact-info">
                <a href={wa("Halo HATTORY KONVEKSI, saya ingin bertanya.")} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 rounded-2xl border border-silver/30 p-5 transition-colors hover:border-navy" data-testid="contact-wa">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Phone className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-widest text-silver">WhatsApp</span><span className="font-medium text-navy">{BRAND.phone}</span></span>
                </a>
                <a href={`mailto:${BRAND.email}`} className="flex items-start gap-4 rounded-2xl border border-silver/30 p-5 transition-colors hover:border-navy">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Mail className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-widest text-silver">Email</span><span className="font-medium text-navy">{BRAND.email}</span></span>
                </a>
                <div className="flex items-start gap-4 rounded-2xl border border-silver/30 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><MapPin className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-widest text-silver">Alamat</span><span className="font-medium text-navy">{BRAND.address}</span></span>
                </div>
                <div className="flex items-start gap-4 rounded-2xl border border-silver/30 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-white"><Clock className="h-5 w-5" /></span>
                  <span><span className="block text-xs uppercase tracking-widest text-silver">Jam Operasional</span><span className="font-medium text-navy">Senin – Sabtu, 08.00 – 17.00 WIB</span></span>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-4">
                <a href={BRAND.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="rounded-full border border-silver/50 p-3 text-navy transition-colors hover:bg-navy hover:text-white"><Instagram className="h-5 w-5" /></a>
                <a href={BRAND.socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="rounded-full border border-silver/50 p-3 text-navy transition-colors hover:bg-navy hover:text-white"><TikTokIcon className="h-5 w-5" /></a>
                <a href={BRAND.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="rounded-full border border-silver/50 p-3 text-navy transition-colors hover:bg-navy hover:text-white"><Facebook className="h-5 w-5" /></a>
              </div>

              <div className="mt-6 overflow-hidden rounded-3xl border border-silver/30 shadow-soft" data-testid="contact-map">
                <iframe
                  title="Lokasi HATTORY KONVEKSI"
                  src={mapSrc}
                  className="h-[300px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.15}>
            <form onSubmit={submit} className="rounded-3xl bg-surface p-8 md:p-10" data-testid="contact-form">
              <h2 className="text-2xl font-black tracking-tight text-navy md:text-3xl">Kirim Pesan</h2>
              <p className="mt-2 text-sm text-charcoal/70">Isi formulir ini, kami akan menghubungi Anda dan lanjut via WhatsApp.</p>

              <div className="mt-8 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-navy">Nama</label>
                  <input value={form.name} onChange={update("name")} data-testid="form-name" className="w-full rounded-xl border border-silver/50 bg-white px-4 py-3 text-charcoal outline-none transition-colors focus:border-navy" placeholder="Nama Anda" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-navy">Kontak (No. WA / Email)</label>
                  <input value={form.contact} onChange={update("contact")} data-testid="form-contact" className="w-full rounded-xl border border-silver/50 bg-white px-4 py-3 text-charcoal outline-none transition-colors focus:border-navy" placeholder="08xx / email@contoh.com" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-navy">Kebutuhan</label>
                  <input value={form.need} onChange={update("need")} data-testid="form-need" className="w-full rounded-xl border border-silver/50 bg-white px-4 py-3 text-charcoal outline-none transition-colors focus:border-navy" placeholder="Contoh: 50 kaos custom sablon" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-navy">Pesan (opsional)</label>
                  <textarea value={form.message} onChange={update("message")} data-testid="form-message" rows={4} className="w-full resize-none rounded-xl border border-silver/50 bg-white px-4 py-3 text-charcoal outline-none transition-colors focus:border-navy" placeholder="Detail tambahan..." />
                </div>
              </div>

              <button
                type="submit"
                data-testid="form-submit"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 font-medium text-white transition-transform duration-300 hover:scale-[1.02] active:scale-95"
              >
                Kirim & Lanjut ke WhatsApp <Send className="h-4 w-4" />
              </button>

              <a
                href={wa("Halo HATTORY KONVEKSI, saya ingin langsung bertanya via WhatsApp.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="form-direct-wa"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-silver px-8 py-4 font-medium text-navy transition-colors hover:bg-white"
              >
                <MessageCircle className="h-4 w-4" /> Chat Langsung via WhatsApp
              </a>
            </form>
          </Reveal>
        </div>
      </div>
    </>
  );
}
